import { Component, Input, OnChanges } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { TimeSeriesPoint } from '../../../models/analytics.model';

@Component({
    selector: 'app-analytics-trend-chart',
    templateUrl: './analytics-trend-chart.component.html',
    styleUrls: ['./analytics-trend-chart.component.scss'],
})
export class AnalyticsTrendChartComponent implements OnChanges {
    @Input() title: string;
    @Input() currentData: TimeSeriesPoint[] = [];
    @Input() previousData: TimeSeriesPoint[];
    @Input() valueField: 'rate' | 'count' = 'rate';
    @Input() color = '#1976d2';
    @Input() targetLine: number | null = null;

    chartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };

    chartOptions: ChartConfiguration<'line'>['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, padding: 16 } },
        },
        scales: {
            x: {
                grid: { display: false },
            },
            y: {
                beginAtZero: true,
            },
        },
        elements: {
            point: { radius: 3, hoverRadius: 5 },
            line: { tension: 0.3 },
        },
    };

    constructor(private translate: TranslateService) {}

    ngOnChanges(): void {
        if (!this.currentData || this.currentData.length === 0) return;

        const labels = this.currentData.map((p) => this.formatDate(p.date));
        const currentValues = this.currentData.map((p) => (this.valueField === 'rate' ? p.rate : p.count) ?? 0);

        const datasets: ChartConfiguration<'line'>['data']['datasets'] = [
            {
                label: this.translate.instant('request_v2.analytics.current_period'),
                data: currentValues,
                borderColor: this.color,
                backgroundColor: this.color + '20',
                pointBackgroundColor: this.color,
                pointBorderColor: this.color,
                fill: true,
            },
        ];

        if (this.previousData && this.previousData.length > 0) {
            const previousValues = this.previousData.map((p) => (this.valueField === 'rate' ? p.rate : p.count) ?? 0);
            datasets.push({
                label: this.translate.instant('request_v2.analytics.previous_period'),
                data: previousValues,
                borderColor: '#d1d5db',
                backgroundColor: 'transparent',
                pointBackgroundColor: '#d1d5db',
                pointBorderColor: '#d1d5db',
                borderDash: [5, 5],
                fill: false,
            });
        }

        this.chartData = { labels, datasets };

        if (this.targetLine !== null) {
            this.chartOptions = {
                ...this.chartOptions,
                plugins: {
                    ...this.chartOptions.plugins,
                    annotation: {
                        annotations: {
                            targetLine: {
                                type: 'line',
                                yMin: this.targetLine,
                                yMax: this.targetLine,
                                borderColor: '#9ca3af',
                                borderWidth: 1,
                                borderDash: [5, 5],
                                label: {
                                    display: true,
                                    content: `${this.translate.instant('request_v2.analytics.target')}: ${this.targetLine}%`,
                                    position: 'end',
                                    backgroundColor: 'transparent',
                                    color: '#9ca3af',
                                    font: { size: 11 },
                                },
                            },
                        },
                    },
                },
            };
        }
    }

    private formatDate(dateStr: string): string {
        if (dateStr.includes('W')) {
            return dateStr;
        }
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
}
